import { selectSelectionData } from '../features/search/searchSelectors';
import { ESelectionType } from '../shared/sharedEnums';
import { IFilterFormData, IPortfolioFormData, IPortFormData } from '../shared/sharedTypes';
import { SelectionFromRoute } from './routingTypes';

export const normalizeSelectionType = (value?: string | null): ESelectionType | undefined => {
	if (!value) return undefined;

	return (Object.values(ESelectionType) as string[]).find((item) => item.toLowerCase() === value.toLowerCase()) as
		| ESelectionType
		| undefined;
};

export const decodeParam = (value?: string | null) => {
	if (!value) return undefined;
	const normalized = value.replace(/\+/g, ' ');
	try {
		return decodeURIComponent(normalized);
	} catch (error) {
		return normalized;
	}
};

export const encodeParam = (value?: string | null) => {
	if (!value) return undefined;
	try {
		return encodeURIComponent(value).replace(/%20/g, '+');
	} catch (error) {
		return value;
	}
};

export const parseSelectionFromState = (selectionData: ReturnType<typeof selectSelectionData>): SelectionFromRoute => {
	const selectionTypeEnum = normalizeSelectionType(selectionData.selectionType);

	switch (selectionTypeEnum) {
		case ESelectionType.Port:
			return {
				type: ESelectionType.Port,
				param: selectionData.selectionName + '_' + (selectionData.selectionTypeObject as IPortFormData).country,
			};
		case ESelectionType.Vessel:
			return {
				type: ESelectionType.Vessel,
				param: selectionData.selectedVesselsList?.[0]?.imoNumber?.toString(),
			};
		case ESelectionType.Portfolio:
		case ESelectionType.DraftPortfolio: {
			const portfolio = selectionData.selectionTypeObject as IPortfolioFormData | undefined;
			return {
				type: selectionTypeEnum,
				param: portfolio?.id?.toString() ?? portfolio?.tempId,
			};
		}
		case ESelectionType.Filter:
		case ESelectionType.DraftFilter: {
			const filter = selectionData.selectionTypeObject as IFilterFormData | undefined;
			return {
				type: selectionTypeEnum,
				param: filter?.id?.toString() ?? filter?.tempId,
			};
		}
		case ESelectionType.Ownership:
		case ESelectionType.Sector:
			return { type: selectionTypeEnum, param: selectionData.selectionName };
		default:
			return {};
	}
};

export const buildPathname = (feature?: string, tab?: string, subtab?: string, selection?: SelectionFromRoute) => {
	// If we have a selection but no feature, create a direct selection route
	if (selection?.type && selection?.param && !feature) {
		return `/${selection.type.toLowerCase()}/${encodeParam(selection.param)}`;
	}

	// If we have both feature and selection, create a feature+selection route with tab/subtab
	if (feature && selection?.type && selection?.param) {
		// Build: /feature/selectionType/selectionParam/tab/subtab
		const segments = [
			feature.toLowerCase(),
			selection.type.toLowerCase(),
			encodeParam(selection.param),
			tab?.toLowerCase(),
			subtab?.toLowerCase(),
		].filter(Boolean);
		return `/${segments.join('/')}`;
	}

	// If we have a feature but no selection
	if (feature) {
		const segments = [feature.toLowerCase(), tab?.toLowerCase(), subtab?.toLowerCase()].filter(Boolean);
		return `/${segments.join('/')}`;
	}

	// Default case - root
	return '/';
};

export const selectionsMatch = (a: SelectionFromRoute, b: SelectionFromRoute) =>
	(a.type ?? '') === (b.type ?? '') && (a.param ?? '') === (b.param ?? '');

export const parseIdParam = (param: string): number | string => (Number.isNaN(Number(param)) ? param : Number(param));
